"use client";

import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";

const unboxingPhotos = [
{
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_1f7c672ab-1772180120880.png",
  alt: "Intel CPU processor in retail box packaging with holographic anti-counterfeit seal label",
  tag: "CPU 處理器",
  caption: "Intel CPU 原廠包裝",
  captionSub: "附防偽全息標籤"
},
{
  src: "/assets/images/IMG_3046-1772188014796.jpg",
  alt: "ASUS ROG Strix GeForce RTX 5090 graphics card unboxing showing retail box and actual GPU",
  tag: "GPU 顯示卡",
  caption: "NVIDIA GPU 開箱實拍",
  captionSub: "原廠封條完整"
},
{
  src: "/assets/images/IMG_3048-1772188141287.jpg",
  alt: "ASUS ROG RTX 5090, motherboards, keyboards, mouse, monitors and other PC components boxes stacked together in original factory packaging",
  tag: "原廠包裝",
  caption: "原廠包裝完整出貨",
  captionSub: "保留原廠保固"
},
{
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_1f628e1dd-1772180123891.png",
  alt: "Close-up of holographic anti-counterfeit security label on computer hardware packaging",
  tag: "防偽標籤",
  caption: "防偽全息標籤特寫",
  captionSub: "可掃碼驗證正品"
},
{
  src: "/assets/images/IMG_3029-1772187783589.jpg",
  alt: "AMD Ryzen 9 9950X processor in clamshell packaging with AMD branding and serial number",
  tag: "CPU 實拍",
  caption: "AMD Ryzen 9 9950X 處理器",
  captionSub: "正品晶片實拍"
},
{
  src: "https://img.rocket.new/generatedImages/rocket_gen_img_13060721e-1772180119863.png",
  alt: "Computer motherboard circuit board with CPU socket RAM slots and PCIe connectors",
  tag: "主機板",
  caption: "主機板細節實拍",
  captionSub: "品質檢測通過"
}];


const authenticityFeatures = [
{
  icon: "ShieldCheckIcon",
  title: "原廠防偽標籤",
  titleEn: "Anti-Counterfeit Labels",
  desc: "每件產品均附原廠防偽全息標籤，可掃碼至官方網站驗證真偽。"
},
{
  icon: "DocumentCheckIcon",
  title: "原廠保固憑證",
  titleEn: "Original Warranty",
  desc: "保留完整原廠保固，CPU/GPU 享有原廠3年保固服務。"
},
{
  icon: "CubeIcon",
  title: "原廠包裝完整",
  titleEn: "Sealed Retail Box",
  desc: "所有產品以原廠封裝出貨，包裝盒完整無損，封條未開封。"
},
{
  icon: "MagnifyingGlassIcon",
  title: "出貨前品質檢測",
  titleEn: "Pre-Ship QC Check",
  desc: "每批貨物出貨前均經過品質檢測，確保產品狀態完好。"
}];


export default function ProductAuthenticitySection() {
  return (
    <section className="py-24 bg-navy-50">
      <div className="max-w-8xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-orange/10 flex items-center justify-center">
              <Icon name="ShieldCheckIcon" size={20} className="text-orange" />
            </div>
            <span className="text-xs font-black text-orange uppercase tracking-widest">正品保證 · Authenticity</span>
          </div>
          <h2 className="font-heading font-black text-3xl md:text-4xl text-navy mb-3">
            開箱實拍 · 正品驗證
          </h2>
          <p className="text-text-muted text-base max-w-2xl mx-auto">
            所有產品均為原廠正品，附防偽標籤及原廠保固。以下為實際產品開箱照片，
            讓您安心採購。
          </p>
        </div>

        {/* Photo Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-5 mb-14">
          {unboxingPhotos.map((photo, i) =>
          <div key={i} className="group overflow-hidden rounded-3xl shadow-md border border-navy-100 bg-white">
              <div className={`relative overflow-hidden ${i === 1 || i === 2 || i === 4 ? 'aspect-[4/3]' : 'h-52'}`}>
                <AppImage
                src={photo.src}
                alt={photo.alt}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              
                <div className="absolute top-3 left-3">
                  <span className="bg-orange text-white text-xs font-bold px-3 py-1 rounded-full">
                    {photo.tag}
                  </span>
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-navy/60 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <div className="text-white font-bold text-sm">{photo.caption}</div>
                  <div className="text-white/70 text-xs">{photo.captionSub}</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Authenticity Features */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {authenticityFeatures.map((feat) =>
          <div key={feat.title} className="bg-white border border-navy-100 rounded-3xl p-6 shadow-sm">
              <div className="w-12 h-12 rounded-2xl bg-orange/10 flex items-center justify-center mb-4">
                <Icon name={feat.icon as "ShieldCheckIcon"} size={22} className="text-orange" />
              </div>
              <div className="font-bold text-navy text-base mb-1">{feat.title}</div>
              <div className="text-xs text-orange font-semibold mb-2">{feat.titleEn}</div>
              <p className="text-text-muted text-sm leading-relaxed">{feat.desc}</p>
            </div>
          )}
        </div>
      </div>
    </section>);

}